import { createClient } from '@supabase/supabase-js';

// Create Supabase client directly for utility functions
const createSupabaseClient = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  
  if (!supabaseUrl || !supabaseKey) {
    throw new Error('Supabase configuration missing. Please check your .env file.');
  }
  
  return createClient(supabaseUrl, supabaseKey);
};

/**
 * Upload PDF blob to Supabase storage
 */
export const uploadPDFToSupabase = async (
  pdfBlob: Blob,
  filename: string,
  quizId?: number,
  email?: string
): Promise<{ url: string; path: string }> => {
  try {
    console.log('📤 Uploading PDF to Supabase...');
    
    const supabase = createSupabaseClient();
    
    // Generate unique filename with timestamp - make it shorter and cleaner
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19); // YYYY-MM-DDTHH-MM-SS
    const randomId = Math.random().toString(36).substr(2, 6); // 6 character random string
    // Remove problematic characters from base filename
    const cleanFilename = filename.replace(/[^a-zA-Z0-9-]/g, '-').replace(/-+/g, '-');
    const uniqueFilename = `${cleanFilename}-${timestamp}-${randomId}.pdf`;
    const filePath = `quiz-pdfs/${uniqueFilename}`;
    
    console.log('📁 Upload path:', filePath);
    console.log('📄 File size:', pdfBlob.size, 'bytes');
    
    // Upload to Supabase storage (this should work with anon key if bucket is public)
    const { data, error } = await supabase.storage
      .from('quiz-pdf-files')
      .upload(filePath, pdfBlob, {
        contentType: 'application/pdf',
        cacheControl: '3600',
        upsert: false
      });
    
    if (error) {
      console.error('❌ Error uploading PDF:', error);
      console.error('❌ Error message:', error.message);
      throw error;
    }
    
    // Get public URL
    const { data: urlData } = supabase.storage
      .from('quiz-pdf-files')
      .getPublicUrl(filePath);
    
    const publicUrl = urlData.publicUrl;
    
    // OPTIONAL: Try to store reference in database, but don't fail if RLS blocks it
    // Note: This is completely optional and the PDF upload succeeds regardless
    if (quizId && email) {
      try {
        console.log('📝 Attempting to save PDF reference to database...');
        const { error: dbError } = await supabase
          .from('pdf_downloads')
          .insert({
            quiz_id: quizId,
            email: email,
            filename: uniqueFilename,
            download_url: publicUrl,
            file_path: filePath,
            download_count: 0,
            created_at: new Date().toISOString()
          });
        
        if (dbError) {
          console.warn('⚠️ Could not save PDF reference to database (RLS policy or missing table):', dbError);
          console.warn('⚠️ This is non-critical - PDF upload still succeeded');
          // PDF upload was successful, so we continue
        } else {
          console.log('✅ PDF reference saved to database');
        }
      } catch (dbError) {
        console.warn('⚠️ Database error (non-critical - PDF upload still succeeded):', dbError);
        // PDF upload was successful, so we continue
      }
    }
    
    console.log('✅ PDF uploaded successfully:', publicUrl);
    
    return {
      url: publicUrl,
      path: filePath
    };
    
  } catch (error) {
    console.error('❌ Failed to upload PDF to Supabase:', error);
    throw error;
  }
};

/**
 * Simple PDF upload to Supabase storage (no database tracking)
 * Use this for testing to avoid RLS issues
 */
export const uploadPDFToSupabaseSimple = async (
  pdfBlob: Blob,
  filename: string
): Promise<{ url: string; path: string }> => {
  try {
    console.log('📤 Uploading PDF to Supabase (simple mode)...');
    
    const supabase = createSupabaseClient();
    
    // Generate unique filename with timestamp - make it shorter and cleaner
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19); // YYYY-MM-DDTHH-MM-SS
    const randomId = Math.random().toString(36).substr(2, 6); // 6 character random string
    // Remove problematic characters from base filename
    const cleanFilename = filename.replace(/[^a-zA-Z0-9-]/g, '-').replace(/-+/g, '-');
    const uniqueFilename = `${cleanFilename}-${timestamp}-${randomId}.pdf`;
    const filePath = `quiz-pdfs/${uniqueFilename}`;
    
    console.log('📁 Upload path:', filePath);
    console.log('📄 File size:', pdfBlob.size, 'bytes');
    console.log('🪣 Target bucket: quiz-pdf-files');
    
    // Check if bucket exists and is accessible
    try {
      const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();
      if (bucketsError) {
        console.warn('⚠️ Could not list buckets:', bucketsError);
      } else {
        const bucketNames = buckets?.map(b => b.name) || [];
        console.log('📦 Available buckets:', bucketNames);
        if (!bucketNames.includes('quiz-pdf-files')) {
          console.error('❌ Bucket "quiz-pdf-files" not found in available buckets');
        }
      }
    } catch (bucketError) {
      console.warn('⚠️ Error checking buckets (continuing anyway):', bucketError);
    }
    
    // Upload to Supabase storage only
    const { data, error } = await supabase.storage
      .from('quiz-pdf-files')
      .upload(filePath, pdfBlob, {
        contentType: 'application/pdf',
        cacheControl: '3600',
        upsert: false
      });
    
    if (error) {
      console.error('❌ Error uploading PDF:', error);
      console.error('❌ Error message:', error.message);
      throw error;
    }
    
    console.log('✅ Upload successful, data:', data);
    
    // Get public URL
    const { data: urlData } = supabase.storage
      .from('quiz-pdf-files')
      .getPublicUrl(filePath);
    
    const publicUrl = urlData.publicUrl;
    
    console.log('✅ PDF uploaded successfully (simple mode):', publicUrl);
    
    return {
      url: publicUrl,
      path: filePath
    };
    
  } catch (error) {
    console.error('❌ Failed to upload PDF to Supabase (simple mode):', error);
    throw error;
  }
};

/**
 * Delete PDF from Supabase storage
 */
export const deletePDFFromSupabase = async (filePath: string): Promise<void> => {
  try {
    const supabase = createSupabaseClient();
    
    const { error } = await supabase.storage
      .from('quiz-pdf-files')
      .remove([filePath]);
    
    if (error) {
      console.error('❌ Error deleting PDF:', error);
      throw error;
    }
    
    console.log('✅ PDF deleted successfully');
  } catch (error) {
    console.error('❌ Failed to delete PDF from Supabase:', error);
    throw error;
  }
};

/**
 * List user's PDFs from Supabase
 */
export const listUserPDFs = async (email: string): Promise<any[]> => {
  try {
    const supabase = createSupabaseClient();
    
    const { data, error } = await supabase
      .from('pdf_downloads')
      .select('*')
      .eq('email', email)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('❌ Error fetching user PDFs:', error);
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('❌ Failed to list user PDFs:', error);
    throw error;
  }
};

/**
 * Update download count for a PDF
 */
export const incrementDownloadCount = async (downloadId: number): Promise<void> => {
  try {
    const supabase = createSupabaseClient();
    
    const { error } = await supabase.rpc('increment_download_count', {
      download_id: downloadId
    });
    
    if (error) {
      console.error('❌ Error updating download count:', error);
      // Don't throw error as this is not critical
    }
  } catch (error) {
    console.error('❌ Failed to update download count:', error);
  }
}; 